import { useEffect, useMemo, useRef, useState } from "react";
import { getWorkflowExpressionByVariableId } from "../../Workflow/WorkflowVariables/services/workflowVariables.service";
import { convertExpressionIdsToNames } from "../../Workflow/WorkflowVariables/utils/expressionHelper";
import { useFormCalculation } from "../contexts/FormCalculationContext";
import { FORM_TABS } from "../FormsConstant";
import { evaluateExpression, findElementIdByVariableId } from "../utils/expressionEvaluator";
import { extractAllDefaultValues, findElementById } from "../utils/formElementHelpers";

/**
 * Custom hook for form expression builder functionality
 * Handles:
 * - Extracting workflow variables from form data
 * - Loading expressions for variables with autoSetValue
 * - Evaluating expressions when form responses change
 * - Updating formResponses based on expression results
 *
 * @param {Object} formData - Form data object (can have formData.content or formData directly)
 * @param {Object} formResponses - Current form responses state
 * @param {Function} setFormResponses - Function to update form responses
 * @param {boolean} isOpen - Whether the form is open (for conditional execution)
 * @param {string} formId - Optional form ID (if not present in formData)
 * @param {Object} formTab - Active form tab object (contains hostSection key). Defaults to SUBSECTIONS_OBJ.
 * @returns {void}
 */
export const useFormExpressionBuilder = (formData, formResponses, setFormResponses, isOpen = true, formId = null, formTab = FORM_TABS.SUBSECTIONS_OBJ) => {
  const { getCalculationValue, updateExpressionEvaluation } = useFormCalculation();
  const defaultsInitializedRef = useRef(false);
  const [expressions, setExpressions] = useState({});

  // Get the actual form content (handle both formData.content and formData directly)
  const formContent = formData?.content || formData;

  const hostSection = formTab?.hostSection || FORM_TABS.SUBSECTIONS_OBJ.hostSection;
  /* Extract workflow variables from form data*/
  const extractedWorkflowVariables = useMemo(() => {
    if (!formContent?.[hostSection]) return [];

    const variables = [];
    const variableMap = new Map();

    formContent[hostSection].forEach((subsection) => {
      if (!subsection.elements) return;

      subsection.elements.forEach((element) => {
        const workflowVariableId = element.properties?.workflowVariableId;
        if (workflowVariableId && !variableMap.has(workflowVariableId)) {
          variableMap.set(workflowVariableId, true);
          variables.push({
            id: workflowVariableId,
            name: element.name,
            type: element.type,
            attributes: element.properties,
          });
        }

        // Check gridElements if element is a row container (2D array: rows and columns)
        if (element.gridElements && Array.isArray(element.gridElements)) {
          element.gridElements.forEach((row) => {
            if (Array.isArray(row)) {
              row.forEach((gridElement) => {
                if (gridElement) {
                  const gridWorkflowVariableId = gridElement.properties?.workflowVariableId;
                  if (gridWorkflowVariableId && !variableMap.has(gridWorkflowVariableId)) {
                    variableMap.set(gridWorkflowVariableId, true);
                    variables.push({
                      id: gridWorkflowVariableId,
                      name: gridElement.name,
                      type: gridElement.type,
                      attributes: gridElement.properties,
                    });
                  }
                }
              });
            }
          });
        }
      });
    });
    return variables;
  }, [formContent, hostSection]);

  /* Initialize formResponses with default values when form opens */
  useEffect(() => {
    if (!isOpen || !formContent) {
      /* Reset flag when form closes*/
      if (!isOpen) {
        defaultsInitializedRef.current = false;
      }
      return;
    }

    /* Only initialize defaults once per form opening*/
    if (defaultsInitializedRef.current) return;

    const defaultValues = extractAllDefaultValues(formContent, hostSection);
    if (Object.keys(defaultValues).length > 0) {
      setFormResponses((prev) => {
        /* Check if we need to update anything*/
        let needsUpdate = false;
        const updated = { ...prev };

        Object.entries(defaultValues).forEach(([fieldId, defaultValue]) => {
          if (updated[fieldId] === undefined) {
            updated[fieldId] = defaultValue;
            needsUpdate = true;
          }
        });

        /* Only return new object if we actually made changes*/
        if (needsUpdate) {
          defaultsInitializedRef.current = true;
        }
        return needsUpdate ? updated : prev;
      });
    } else {
      /* Mark as initialized even if no defaults*/
      defaultsInitializedRef.current = true;
    }
  }, [isOpen, formContent, setFormResponses]);

  /* Load expressions for variables with autoSetValue*/
  useEffect(() => {
    if (!isOpen || !formContent) return;

    const loadExpressions = async () => {
      const expressionsMap = {};

      const autoSetElements = [];
      formContent[hostSection]?.forEach((subsection) => {
        subsection.elements?.forEach((element) => {
          if (element.properties?.autoSetValue && element.properties?.workflowVariableId) {
            autoSetElements.push(element);
          }

          // Check gridElements if element is a row container (2D array: rows and columns)
          if (element.gridElements && Array.isArray(element.gridElements)) {
            element.gridElements.forEach((row) => {
              if (Array.isArray(row)) {
                row.forEach((gridElement) => {
                  if (gridElement && gridElement.properties?.autoSetValue && gridElement.properties?.workflowVariableId) {
                    autoSetElements.push(gridElement);
                  }
                });
              }
            });
          }
        });
      });

      for (const element of autoSetElements) {
        const workflowVariableId = element.properties.workflowVariableId;
        const params = {
          workflowVariableId: workflowVariableId,
        };

        const formIdFromData = formId || formData?._id || formData?.id;
        if (formIdFromData) {
          params.formId = formIdFromData;
        }
        const expressionData = await getWorkflowExpressionByVariableId(params);

        if (expressionData?.expression) {
          expressionsMap[workflowVariableId] = expressionData.expression;
        } else if (expressionData?.rawExpression) {
          const expressionWithNames = convertExpressionIdsToNames(expressionData.rawExpression, extractedWorkflowVariables);
          expressionsMap[workflowVariableId] = expressionWithNames;
        }
      }

      setExpressions(expressionsMap);
    };

    loadExpressions();
  }, [isOpen, formContent, extractedWorkflowVariables, formData]);

  /* Evaluate expressions when form responses change*/
  useEffect(() => {
    if (!formContent || Object.keys(expressions).length === 0) return;

    /* Collect all updates first, then apply them in a single setState call*/
    const updates = {};

    Object.entries(expressions).forEach(([workflowVariableId, expression]) => {
      const result = evaluateExpression(expression, formResponses, formContent, extractedWorkflowVariables, hostSection);
      updateExpressionEvaluation(workflowVariableId, result);

      const elementId = findElementIdByVariableId(workflowVariableId, formContent, hostSection);
      if (elementId) {
        const element = findElementById(elementId, formContent, hostSection);
        if (element && !element.properties?.isHidden) {
          if (typeof result === "number") {
            /* Only update if the value has actually changed*/
            const currentValue = formResponses[elementId];
            const currentValueNum = currentValue !== undefined && currentValue !== null && currentValue !== "" ? parseFloat(currentValue) : undefined;

            /* Check if value has changed (handle NaN cases)*/
            const hasChanged = currentValueNum !== result && !(isNaN(currentValueNum) && isNaN(result));

            if (hasChanged) {
              updates[elementId] = result;
            }
          }
        }
      }
    });

    /* Only update if there are actual changes*/
    if (Object.keys(updates).length > 0) {
      setFormResponses((prev) => ({
        ...prev,
        ...updates,
      }));
    }
  }, [formResponses, expressions, formContent, extractedWorkflowVariables, updateExpressionEvaluation, setFormResponses]);
};

